import React from 'react';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';

const tiers = [
  {
    name: 'Starter',
    price: 19.99,
    description: 'Perfect for small contractors getting started with government contracting.',
    features: [
      'Up to 5 team members',
      'Basic AI contract analysis',
      'Compliance checks',
      'Team collaboration tools',
      'Email support',
      '5 proposals per month',
      'Basic templates',
      'Standard reports',
    ],
    cta: 'Start free trial',
    mostPopular: false,
  },
  {
    name: 'Professional',
    price: 79.99,
    description: 'Ideal for growing contractors with multiple ongoing proposals.',
    features: [
      'Up to 15 team members',
      'Advanced AI insights',
      'Real-time compliance monitoring',
      'Priority support',
      'Custom templates',
      'Analytics dashboard',
      'Unlimited proposals',
      'Advanced reporting',
      'API access',
      'Dedicated account manager',
    ],
    cta: 'Start free trial',
    mostPopular: true,
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    description: 'For large organizations with complex contracting needs.',
    features: [
      'Unlimited team members',
      'Custom AI model training',
      'Advanced analytics',
      'Dedicated support',
      'Custom integrations',
      'SLA guarantees',
      'On-premise deployment',
      'Custom workflow automation',
      'Advanced security features',
      'Compliance automation',
    ],
    cta: 'Contact sales',
    mostPopular: false,
  },
];

export function Pricing() {
  return (
    <div className="bg-gray-50 py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Simple, Transparent Pricing
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            Choose the plan that best fits your contracting needs
          </p>
        </div>

        <div className="mx-auto mt-16 grid max-w-lg grid-cols-1 gap-8 lg:max-w-none lg:grid-cols-3">
          {tiers.map((tier) => (
            <div
              key={tier.name}
              className={`flex flex-col rounded-3xl bg-white p-8 shadow-xl ring-1 ring-gray-200 ${
                tier.mostPopular ? 'relative lg:scale-105 lg:shadow-2xl' : ''
              }`}
            >
              {tier.mostPopular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 rounded-full bg-indigo-600 px-4 py-1 text-sm font-semibold text-white">
                  Most Popular
                </div>
              )}
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900">{tier.name}</h3>
                <p className="mt-4 flex items-baseline">
                  {typeof tier.price === 'number' ? (
                    <>
                      <span className="text-4xl font-bold tracking-tight text-gray-900">
                        ${tier.price}
                      </span>
                      <span className="ml-1 text-sm font-semibold text-gray-600">/month</span>
                    </>
                  ) : (
                    <span className="text-4xl font-bold tracking-tight text-gray-900">
                      {tier.price}
                    </span>
                  )}
                </p>
                <p className="mt-6 text-sm text-gray-600">{tier.description}</p>

                <ul className="mt-8 space-y-3">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start">
                      <Check className="h-5 w-5 flex-shrink-0 text-indigo-600" />
                      <span className="ml-3 text-sm text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Link
                to={tier.price === 'Custom' ? '/contact' : '/dashboard'}
                className={`mt-8 block rounded-md px-3.5 py-2 text-center text-sm font-semibold leading-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
                  tier.mostPopular
                    ? 'bg-indigo-600 text-white hover:bg-indigo-500 focus-visible:outline-indigo-600'
                    : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'
                }`}
              >
                {tier.cta}
              </Link>
            </div>
          ))}
        </div>

        {/* Money Back Guarantee */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-500">
            30-day money-back guarantee • No credit card required • Cancel anytime
          </p>
        </div>
      </div>
    </div>
  );
}